import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { integratedLanguageService } from '../services/integratedLanguageService';

// Define available languages - 11 initially supported languages
export const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
];

// Supported languages for the app (have translations)
export const supportedLanguages = ['en', 'es', 'fr', 'hi', 'de', 'it', 'ja', 'nl', 'pt', 'ru', 'ar'];

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (lang: string) => Promise<void>;
  languages: typeof languages;
  isLoadingLanguage: boolean;
  forceUpdate: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Force re-render trigger

  /**
   * Set language with lazy loading for 24+ language support
   */
  const setLanguage = async (newLanguage: string): Promise<void> => {
    if (currentLanguage === newLanguage) {
      return; // No change needed
    }

    try {
      // Set loading state
      setIsLoadingLanguage(true);
      
      // Use integrated service for lazy loading
      await integratedLanguageService.changeLanguage(newLanguage);
      
      // Update i18n language
      await i18n.changeLanguage(newLanguage);
      
      // Update context state
      setCurrentLanguage(newLanguage);
      
      // Save to localStorage
      localStorage.setItem('language', newLanguage);
      
      // Preload only 1-2 additional languages (not aggressive preloading)
      const popularLanguages = ['en', 'es', 'fr']; // Most commonly used
      const languagesToPreload = popularLanguages
        .filter(lang => lang !== newLanguage)
        .slice(0, 1); // Only preload 1 additional language
      
      if (languagesToPreload.length > 0) {
        integratedLanguageService.preloadLanguages(languagesToPreload);
      }
      
    } catch (error) {
      console.error(`❌ LanguageContext: Error changing language to ${newLanguage}:`, error);
      // Fallback to English
      await i18n.changeLanguage('en');
      setCurrentLanguage('en');
      localStorage.setItem('language', 'en');
    } finally {
      // Clear loading state
      setIsLoadingLanguage(false);
    }
  };

  const forceUpdate = () => {
    setUpdateTrigger(prev => prev + 1);
  };

  const updateAlternateLanguageLinks = (currentLang: string) => {
    // Remove existing alternate links
    const existingLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingLinks.forEach(link => link.remove());

    // Add new alternate links
    languages.forEach(lang => {
      if (lang.code !== currentLang) {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = lang.code;
        link.href = `${window.location.origin}/${lang.code}${window.location.pathname}`;
        document.head.appendChild(link);
      }
    });
  };

  useEffect(() => {
    // Sync the current language state with i18n's actual language
    // This ensures the UI shows the correct language even if i18n was initialized with a different language
    const syncLanguage = () => {
      const actualLanguage = i18n.language;
  
      if (actualLanguage !== currentLanguage) {
        
        setCurrentLanguage(actualLanguage);
        // Force a re-render when syncing
        setUpdateTrigger(prev => prev + 1);
      }
    };

    // Initial sync
    syncLanguage();

    // Listen for language changes
    const handleLanguageChanged = () => {
  
      syncLanguage();
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, currentLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, languages, isLoadingLanguage, forceUpdate }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 